import React from 'react';
import PropTypes from 'prop-types';
import styles from './RemediateModal.module.css';

export const RemediateModal = ({ 
  isOpen, 
  onClose, 
  ticket, 
  onApprove, 
  isLoading 
}) => {
  if (!isOpen || !ticket) return null;

  // Helper function to format remediation description
  const formatRemediationDescription = (description) => {
    if (!description) return 'No remediation description available';
    
    // Remove the "**Remediation Suggestion**" heading if it exists
    let formattedText = description.replace(/\*\*Remediation Suggestion\*\*/gi, '').trim();
    
    // Also remove any standalone colons that might be left over from headers
    formattedText = formattedText.replace(/^:\s*/gm, '');
    
    const formatBulletPoints = (text) => {
      // Convert common bullet point patterns to proper bullet points
      // Be very specific to avoid converting labels/headings that end with colons
      return text
        .replace(/^[-*•]\s+/gm, '• ') // Standardize existing bullet points
        .replace(/^\d+\.\s+/gm, '• ') // Convert numbered lists (1. 2. 3.) - this removes the number and period
        // Only convert single letters for lists (a. b. c.) but not labels ending with colon
        .replace(/^([a-z])\.\s+(?!.*:)/gmi, '• ')
        // Clean up any remaining orphaned periods or colons after bullet conversion
        .replace(/^•\s*[.:]\s*/gm, '• '); 
    };
    
    formattedText = formatBulletPoints(formattedText);
    
    return formattedText;
  };

  // Helper function to render formatted description with proper line breaks
  const renderFormattedDescription = (description) => {
    const formatted = formatRemediationDescription(description);
    const lines = formatted.split('\n').filter(line => line.trim());
    
    const elements = [];
    let currentBullets = [];
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('•')) {
        // Collect bullet points
        currentBullets.push(trimmedLine.substring(1).trim());
      } else {
        // If we have collected bullets, create a ul element
        if (currentBullets.length > 0) {
          elements.push(
            <ul key={`bullets-${elements.length}`} className={styles.bulletList}>
              {currentBullets.map((bullet, bulletIndex) => (
                <li key={`bullet-${bulletIndex}-${bullet.slice(0, 10)}`} className={styles.bulletPoint}>
                  {bullet}
                </li>
              ))}
            </ul>
          );
          currentBullets = [];
        }
        
        // Add paragraph
        if (trimmedLine) {
          elements.push(
            <p key={`para-${index}-${trimmedLine.slice(0, 10)}`} className={styles.descriptionParagraph}>
              {trimmedLine}
            </p>
          );
        }
      }
    });
    
    // Don't forget any remaining bullets
    if (currentBullets.length > 0) {
      elements.push(
        <ul key={`bullets-${elements.length}`} className={styles.bulletList}>
          {currentBullets.map((bullet, bulletIndex) => (
            <li key={`bullet-${bulletIndex}-${bullet.slice(0, 10)}`} className={styles.bulletPoint}>
              {bullet}
            </li>
          ))}
        </ul>
      );
    }
    
    return elements;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleApprove = async () => {
    if (ticket.remediation?.action_id && ticket.alertId) {
      // Pass both action_id and specific alert_id to the approve handler
      await onApprove(ticket.remediation.action_id, ticket.alertId);
    }
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>Confirm Remediation</h3>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>
        
        <div className={styles.content}>
          <div className={styles.ticketInfo}>
            <div className={styles.ticketId}>
              <span className={styles.label}>Ticket ID:</span>
              <span className={styles.value}>{ticket.id}</span>
            </div>
            <div className={styles.ticketTitle}>
              <span className={styles.label}>Title:</span>
              <span className={styles.value}>{ticket.title}</span>
            </div>
          </div>
          
          <div className={styles.description}>
            <h4 className={styles.descriptionTitle}>Remediation Description</h4>
            <div className={styles.remediationContent}>
              <div className={styles.formattedDescription}>
                {renderFormattedDescription(
                  ticket.remediation?.description || 
                  ticket.remediation?.recommended_action || 
                  'No remediation description available'
                )}
              </div>

              {ticket.remediation?.action_title && (
                <div className={styles.actionTitleSection}>
                  <h5 className={styles.actionTitleLabel}>Action Title:</h5>
                  <p className={styles.actionTitleText}>{ticket.remediation.action_title}</p>
                </div>
              )}
              
              {ticket.remediation?.ai_action && (
                <div className={styles.aiActionContainer}>
                  <h5 className={styles.aiActionTitle}>AI Action Steps</h5>
                  {typeof ticket.remediation.ai_action === 'string' ? (
                    <p className={styles.aiActionText}>{ticket.remediation.ai_action}</p>
                  ) : (
                    <>
                      {ticket.remediation.ai_action.goal && (
                        <p className={styles.aiActionGoal}>{ticket.remediation.ai_action.goal}</p>
                      )}
                      {ticket.remediation.ai_action.steps && Array.isArray(ticket.remediation.ai_action.steps) && (
                        <ul className={styles.aiActionSteps}>
                          {ticket.remediation.ai_action.steps.map((stepObj, stepIndex) => {
                            const stepText = stepObj.step || stepObj;
                            return (
                              <li key={`step-${stepIndex}-${String(stepText).slice(0, 10)}`} className={styles.aiActionStep}>
                                {stepText}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {ticket.remediation?.action_id && (
            <div className={styles.actionInfo}>
              <span className={styles.label}>Action ID:</span>
              <span className={styles.actionId}>{ticket.remediation.action_id}</span>
            </div>
          )}
        </div>
        
        <div className={styles.footer}>
          <button 
            className={styles.cancelButton} 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            className={styles.approveButton} 
            onClick={handleApprove}
            disabled={isLoading || !ticket.remediation?.action_id}
          >
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                Approving...
              </div>
            ) : (
              'Approve'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

RemediateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ticket: PropTypes.object,
  onApprove: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

RemediateModal.defaultProps = {
  isLoading: false,
};